import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, catchError, throwError, of } from 'rxjs';
import { environment } from '../environments/environment';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root',
})
export class StrapiService {
  private readonly apiUrl = environment.strapiApiUrl.replace(/\/+$|\/+$/, '');
  private readonly httpClient = inject(HttpClient);
private readonly languageService = inject(LanguageService);

    getPage(page: string): Observable<any> {
    const locale = this.languageService.getCurrentLanguage();

    return this.httpClient.get<any>(
      `${this.apiUrl}/pages?filters[slug][$eq]=${page}&locale=${locale}`
    ).pipe(map(res => res?.data?.[0]), catchError(this.handleError));
  }


  sendContactMail(formData: any) {
    return this.httpClient.post(`${this.apiUrl}/pages/sendContactMail`, formData).pipe(map<any, any[]>(res => res?.data ?? []), catchError(this.handleError));
  }

  /**
   * Haalt het announcement op dat op dit moment als popup getoond mag worden.
   * Geeft null terug wanneer er geen actieve popup is; fouten worden bewust
   * opgeslikt zodat een falende call de site nooit blokkeert.
   */
  getPopupAnnouncement(language: string): Observable<any> {
    return this.httpClient.get<any>(
      `${this.apiUrl}/popup-announcement?locale=${language}`
    ).pipe(
      map(res => res?.data ?? null),
      catchError((error) => {
        console.warn('Failed to get popup announcement', error);

        return of(null);
      })
    );
  }

  getEventInfo(event: string | undefined): Observable<any> {
    return this.httpClient.get<any>(
      `${this.apiUrl}/event-infos?filters[slug][$eq]=${event}`
    ).pipe(map(res => res?.data?.[0]), catchError(this.handleError));
  }

  // Nieuwe methode om pagina op te halen op basis van slug en specifieke taal
  getPageById(slug: string, language: string): Observable<any> {
    const url = `${this.apiUrl}/pages?filters[slug][$eq]=${slug}&locale=${language}&populate=localizations`;
    console.log('Strapi API call URL:', url);
    
    return this.httpClient.get<any>(url).pipe(
      map(res => {
        console.log('Strapi API response for getPageById:', res);
        console.log('Pages data:', res?.data);
        console.log('First page:', res?.data?.[0]);
        return res?.data?.[0];
      }),
      catchError(this.handleError)
    );
  }

  // Nieuwe methode om vertaling te vinden op basis van documentId (Strapi 5)
  getPageTranslation(documentId: string, targetLanguage: string): Observable<any> {
    const url = `${this.apiUrl}/pages?filters[documentId][$eq]=${documentId}&locale=${targetLanguage}`;
    console.log('Strapi getPageTranslation URL:', url);
    
    return this.httpClient.get<any>(url).pipe(
      map(res => {
        console.log('Strapi translation response:', res);
        return res?.data?.[0];
      }),
      catchError(this.handleError)
    );
  }

  // Methode die alle lokaliseringen in één keer ophaalt
  getPageWithAllLocalizations(slug: string, language: string): Observable<any> {
    return this.httpClient.get<any>(
      `${this.apiUrl}/pages?filters[slug][$eq]=${slug}&locale=${language}&populate=localizations`
    ).pipe(
      map(res => {
        const page = res?.data?.[0];
        if (page?.localizations) {
          // Maak een map van taal naar slug voor alle vertalingen
          const translations: { [key: string]: string } = {};
          translations[language] = page.slug; // Huidige pagina
          
          page.localizations.forEach((loc: any) => {
            translations[loc.locale] = loc.slug;
          });
          
          return { ...page, translations };
        }
        return page;
      }),
      catchError(this.handleError)
    );
  }

  // Get global data (header, footer, etc.) for specific language
  getGlobalData(language: string): Observable<any> {
    const url = `${this.apiUrl}/global?populate=deep&locale=${language}`;
    console.log('Getting global data for language:', language, 'URL:', url);
    
    return this.httpClient.get<any>(url).pipe(
      map(res => {
        console.log('Global data response:', res);
        return res?.data || res;
      }),
      catchError((error) => {
        console.warn(`Failed to get global data for ${language}, trying default locale...`, error);
        // Fallback to default locale (nl) if specific locale fails
        if (language !== 'nl') {
          return this.httpClient.get<any>(`${this.apiUrl}/global?populate=deep&locale=nl`).pipe(
            map(res => {
              console.log('Fallback global data response:', res);
              return res?.data || res;
            }),
            catchError(this.handleError)
          );
        }
        return this.handleError(error);
      })
    );
  }

  private handleError(error: HttpErrorResponse) {
    console.error('API error occurred:', error);

    return throwError(() => new Error('Something went wrong!'));
  }
}
